import React from 'react';

import { Button } from 'primereact/button';
import { InputSwitch } from 'primereact/inputswitch';
import { InputText } from 'primereact/inputtext';
import { Tooltip } from 'primereact/tooltip';
import { useFormContext } from 'react-hook-form';
import { PickerTexts } from 'utils';
import { markDeletedItems } from 'utils/markDeletedItems';

import { CustomDropdown } from 'ui/custom-controls';

import { DetailedInformationFormType } from '../../constants/schemas';
import {
  ComplexRoleUI,
  DepartmentTaskType,
  DropdownSelectorType
} from '../../types/interfaces';
import { TagsComponent } from '../TagsComponent';

import { EditableTagsComponent } from './EditableTagsComponent';

const priorityOptions = [
  { id: 0, label: 'Низкий' },
  { id: 1, label: 'Средний' },
  { id: 2, label: 'Высокий' }
];

interface MainTabProps {
  itemType: string;
  hasChildren?: boolean;

  taskTypesOptions: DepartmentTaskType[];
  localTaskTypes: DepartmentTaskType[];
  complexRoleOptions: ComplexRoleUI[];
  localUserRole: ComplexRoleUI[];
  localUserRoleTags: DropdownSelectorType[];

  setLocalDirty: React.Dispatch<React.SetStateAction<boolean>>;
  handleInputChange: (
    field: keyof DetailedInformationFormType
  ) => Promise<void>;
  handleProductivityChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleIntervalDurationChange: (
    e: React.ChangeEvent<HTMLInputElement>
  ) => void;
  calculateAverageProductivity: () => void;
  handleTskTypesSelectionChange: (
    selection: DepartmentTaskType | DepartmentTaskType[] | null
  ) => void;
  handleDeleteTaskType: (taskTypeId: string | number) => void;
  handleEditTaskType: (taskTypeId: string | number) => void;
  handleSelectionChange: (
    selection: ComplexRoleUI | ComplexRoleUI[] | null
  ) => void;
  handleDeleteRole: (roleId: string | number) => void;
}

const MainTab: React.FC<MainTabProps> = ({
  itemType,
  hasChildren,
  taskTypesOptions,
  localTaskTypes,
  complexRoleOptions,
  localUserRole,
  localUserRoleTags,
  setLocalDirty,
  handleInputChange,
  handleProductivityChange,
  handleIntervalDurationChange,
  calculateAverageProductivity,
  handleTskTypesSelectionChange,
  handleDeleteTaskType,
  handleEditTaskType,
  handleSelectionChange,
  handleDeleteRole
}) => {
  const {
    register,
    watch,
    setValue,
    trigger,
    formState: { errors }
  } = useFormContext<DetailedInformationFormType>();

  const parentKey = watch('parentKey');
  const taskTypes = watch('taskTypes');
  const userRole = watch('userRole');
  const isCustomProductivity = watch('isCustomProductivity');
  const isIntervalType = watch('isIntervalType');

  return (
    <div className="flex flex-column gap-3 pb-0">
      <div className="flex flex-column">
        <div className="flex flex-column">
          <div className="flex flex-column">
            <div className="flex flex-row justify-content-between align-items-center gap-3">
              <label style={{ minWidth: '165px' }} htmlFor="label">
                Название<span className="required">*</span>:
              </label>
              <InputText
                className="detailed-card__input w-full flex-grow"
                {...register('label', {
                  onChange: () => handleInputChange('label')
                })}
              />
            </div>
            <p
              className={`detailed-card-errors card-errors ${
                errors.label ? 'text-red-500' : 'text-transparent'
              } `}
            >
              {errors.label ? errors.label.message : ' '}
            </p>
          </div>

          {hasChildren && (
            <div className="flex flex-column">
              <div className="flex flex-row justify-content-between align-items-center gap-3">
                <label htmlFor="parentDepartment" style={{ minWidth: '165px' }}>
                  Подразделение:
                </label>
                <InputText
                  disabled
                  className="detailed-card__input w-full"
                  value={parentKey ? watch('organization') || '' : ''}
                />
              </div>
            </div>
          )}

          {!hasChildren && (
            <>
              <div
                className="flex flex-column"
                style={{
                  height: itemType === 'Подразделение' ? '0' : 'auto',
                  visibility:
                    itemType === 'Подразделение' ? 'hidden' : 'visible'
                }}
              >
                <div className="flex flex-row justify-content-between align-items-center gap-3">
                  <label htmlFor="organization" style={{ minWidth: '165px' }}>
                    Подразделение:
                  </label>
                  <InputText
                    disabled
                    className="detailed-card__input w-full"
                    {...register('organization', {
                      onChange: () => handleInputChange('organization')
                    })}
                  />
                </div>
                <p
                  className={`detailed-card-errors card-errors ${
                    errors.organization ? 'text-red-500' : 'text-transparent'
                  } `}
                >
                  {errors.organization ? errors.organization.message : ' '}
                </p>
              </div>

              <div
                className="flex flex-column"
                style={{
                  height: itemType === 'Подразделение' ? '0' : 'auto',
                  visibility:
                    itemType === 'Подразделение' ? 'hidden' : 'visible'
                }}
              >
                <div className="flex flex-row justify-content-between align-items-center gap-3">
                  <label htmlFor="priority" style={{ minWidth: '165px' }}>
                    Приоритет:
                  </label>
                  <div className="w-full flex gap-2 align-items-center">
                    <CustomDropdown
                      assetOptions={priorityOptions}
                      selectedAsset={
                        priorityOptions.find(
                          (opt) => opt.id === watch('priority')
                        ) ?? null
                      }
                      onSelectionChange={(selected) => {
                        setLocalDirty(true);
                        const priorityValue =
                          selected !== null &&
                          (selected as any).id !== undefined
                            ? Number((selected as any).id)
                            : undefined;
                        setValue('priority', priorityValue, {
                          shouldDirty: true,
                          shouldValidate: true
                        });
                        trigger('priority');
                      }}
                      placeholder="Выберите приоритет"
                      showClearOnHover
                    />
                  </div>
                </div>
                <p
                  className={`detailed-card-errors card-errors ${
                    errors.priority ? 'text-red-500' : 'text-transparent'
                  }`}
                >
                  {errors.priority?.message || ' '}
                </p>
              </div>

              <div
                className="flex flex-column"
                style={{
                  height: itemType === 'Подразделение' ? '0' : 'auto',
                  visibility:
                    itemType === 'Подразделение' ? 'hidden' : 'visible'
                }}
              >
                <div className="flex flex-row justify-content-between align-items-center gap-3">
                  <label style={{ minWidth: '165px' }} htmlFor="productivity">
                    Производительность:
                    {!isCustomProductivity ? (
                      <>
                        <i
                          className="pi pi-exclamation-circle closing-status-tooltip"
                          style={{
                            fontSize: '1rem',
                            color: 'rgb(255 168 0)',
                            cursor: 'pointer',
                            marginLeft: 10
                          }}
                          data-pr-tooltip="Средняя производительность сотрудников рассчитана автоматически (наряды / час)"
                        />
                        <Tooltip target=".closing-status-tooltip" />
                      </>
                    ) : (
                      <>
                        <i
                          id="productivity-tooltip"
                          className="pi pi-question-circle"
                          style={{ cursor: 'pointer', marginLeft: 10 }}
                        />
                        <Tooltip
                          target="#productivity-tooltip"
                          content="наряды / час"
                        />
                      </>
                    )}
                  </label>
                  <InputText
                    className="detailed-card__input w-full"
                    type="number"
                    value={watch('productivity')?.toString() || '0'}
                    onChange={handleProductivityChange}
                  />
                  <Button
                    className="functional-structure-save-btn__roles"
                    icon="pi pi-calculator"
                    tooltip="Рассчитать среднюю производительность сотрудников"
                    tooltipOptions={{ position: 'top' }}
                    onClick={calculateAverageProductivity}
                    style={{ minWidth: 42 }}
                  />
                </div>
                <p
                  className={`detailed-card-errors card-errors ${
                    errors.productivity ? 'text-red-500' : 'text-transparent'
                  } `}
                >
                  {errors.productivity ? errors.productivity.message : ' '}
                </p>
              </div>
            </>
          )}

          <div
            className="flex flex-column"
            style={{
              height: itemType === 'Подразделение' ? '0' : 'auto',
              visibility: itemType === 'Подразделение' ? 'hidden' : 'visible'
            }}
          >
            <div className="flex flex-row justify-content-between align-items-center gap-3">
              <label
                style={{
                  minWidth: '165px',
                  maxWidth: '165px',
                  wordWrap: 'break-word'
                }}
                htmlFor="taskTypes"
              >
                Типы нарядов, выполняемые участком:
              </label>
              <div style={{ width: '100%' }}>
                <CustomDropdown
                  multiplySelectMode={true}
                  assetOptions={taskTypesOptions}
                  selectedAsset={localTaskTypes}
                  onSelectionChange={handleTskTypesSelectionChange}
                  fieldName="label"
                  placeholder={
                    localTaskTypes.length === 0
                      ? 'Укажите типы нарядов'
                      : PickerTexts.taskTypes(localTaskTypes.length)
                  }
                  emptyMessage="Нет типов нарядов для выбора."
                  minWidth={415}
                  showDeletedWarning
                  deletedWarningTooltip="Некоторые типы нарядов удалены в справочнике"
                />
              </div>
            </div>
            <p
              className={`detailed-card-errors card-errors ${
                errors.taskTypes ? 'text-red-500' : 'text-transparent'
              } `}
            >
              {/* {errors.userRole ? errors.userRole.message : ' '} */}
            </p>
          </div>

          {taskTypes && taskTypes.length > 0 && (
            <EditableTagsComponent
              initialTagsItems={markDeletedItems(
                taskTypes.map((taskType: any) => ({
                  key: taskType.id,
                  label: taskType.label
                })),
                taskTypesOptions
              )}
              onDelete={handleDeleteTaskType}
              onEdit={handleEditTaskType}
              passedClassName="mb-3 gap-3"
              editable={isIntervalType}
            />
          )}

          <div
            className="flex flex-column"
            style={{
              height: itemType === 'Подразделение' ? '0' : 'auto',
              visibility: itemType === 'Подразделение' ? 'hidden' : 'visible'
            }}
          >
            <div className="flex flex-row justify-content-between align-items-center gap-3">
              <label style={{ minWidth: '165px' }} htmlFor="isIntervalType">
                Интервальный участок:
              </label>
              <div className="w-full flex align-items-center gap-3">
                <InputSwitch
                  checked={isIntervalType || false}
                  onChange={(e) => {
                    const newValue = e.value ?? false;
                    setValue('isIntervalType', newValue, {
                      shouldDirty: true,
                      shouldValidate: true
                    });
                    setLocalDirty(true);
                  }}
                />
              </div>
            </div>
            <p className={`detailed-card-errors card-errors text-transparent`}>
              {' '}
            </p>
          </div>

          <div
            className="flex flex-column"
            style={{
              height:
                itemType === 'Подразделение' || !isIntervalType ? '0' : 'auto',
              visibility:
                itemType === 'Подразделение' || !isIntervalType
                  ? 'hidden'
                  : 'visible'
            }}
          >
            <div className="flex flex-row justify-content-between align-items-center gap-3">
              <label style={{ maxWidth: '165px', minWidth: '165px' }}>
                Продолжительность интервала (ч):
                <span className="required">*</span>
              </label>
              <div className="w-full flex align-items-center gap-3">
                <div style={{ width: '100%' }}>
                  <InputText
                    className={`detailed-card__input w-full ${
                      errors.intervalDuration ? 'p-invalid' : ''
                    }`}
                    type="number"
                    value={watch('intervalDuration')?.toString() ?? ''}
                    onChange={handleIntervalDurationChange}
                  />
                </div>
              </div>
            </div>
            <p
              className={`detailed-card-errors card-errors ${
                errors.intervalDuration ? 'text-red-500' : 'text-transparent'
              }`}
            >
              {errors.intervalDuration ? errors.intervalDuration.message : ' '}
            </p>
          </div>

          <div
            className="flex flex-column"
            style={{
              height: itemType === 'Подразделение' ? '0' : 'auto',
              visibility: itemType === 'Подразделение' ? 'hidden' : 'visible'
            }}
          >
            <div className="flex flex-row justify-content-between align-items-center gap-3">
              <label style={{ minWidth: '165px' }} htmlFor="userRole">
                Роль:
              </label>
              <div style={{ width: '100%' }}>
                <CustomDropdown
                  multiplySelectMode={true}
                  assetOptions={complexRoleOptions}
                  selectedAsset={localUserRole}
                  onSelectionChange={handleSelectionChange}
                  fieldName="name"
                  placeholder={
                    localUserRoleTags.length === 0
                      ? 'Укажите роли'
                      : PickerTexts.roles(localUserRoleTags.length)
                  }
                  emptyMessage="Нет ролей для выбора."
                  minWidth={415}
                  showDeletedWarning
                  deletedWarningTooltip="Некоторые роли удалены в справочнике"
                />
              </div>
            </div>
            <p
              className={`detailed-card-errors card-errors ${
                errors.userRole ? 'text-red-500' : 'text-transparent'
              } `}
            >
              {/* {errors.userRole ? errors.userRole.message : ' '} */}
            </p>
          </div>
        </div>
        <TagsComponent
          initialTagsItems={markDeletedItems(
            (userRole || []).map((role: any) => ({
              key: role.id,
              label: role.name
            })),
            complexRoleOptions
          )}
          onDelete={handleDeleteRole}
        />
      </div>
    </div>
  );
};

export default MainTab;
